"use client";
import dynamic from "next/dynamic";
import Navigation from "@/HomeFeatures/Navigation";
import Image from "next/image";
import { useState } from "react";
import Locations from "@/HomeFeatures/Contact/Locations";
import { Images, Videos } from "@assets/assets";
import DeferredVideo from "@/components/DeferredVideo";

// PERF: removed eager `gsap`/`ScrollTrigger` imports + module-scope
// registerPlugin — this component does not use GSAP anywhere, so they only
// added ~70KB to the route bundle and ran on load for nothing.
const ContactSection = dynamic(() => import("@/HomeFeatures/Contact2"));

export default function ServicePage() {
  const [calendarOpen, setCalendarOpen] = useState(false);

  const ratingsData = [
    {
      id: 1,
      title: "5.0 Rated in Designrush",
      img: Images?.contactUs.designrushPng,
      alt: "Designrush",
    },
    {
      id: 2,
      title: "5.0 Rated in G2",
      img: Images?.contactUs.g2,
      alt: "G2",
    },
    {
      id: 3,
      title: "5.0 Rated in Goodfirms",
      img: Images?.contactUs.goodfirms,
      alt: "Goodfirms",
    },
    {
      id: 4,
      title: "5.0 Rated in Clutch",
      img: Images?.contactUs.clutch2,
      alt: "Clutch",
    },
  ];
  return (
    <>
      <style jsx global>{`
        @keyframes soft-jump {
          0%,
          100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }
        @keyframes glow-pulse {
          0%,
          100% {
            filter: drop-shadow(0 0 2px rgba(255, 255, 255, 0.3));
          }
          50% {
            filter: drop-shadow(0 0 12px rgba(255, 255, 255, 0.4));
          }
        }
        .animate-jump {
          animation:
            soft-jump 2s infinite ease-in-out,
            glow-pulse 2s infinite ease-in-out;
        }
        .animate-jump:hover {
          animation-play-state: paused;
        }
      `}</style>

      <section className="relative flex flex-col bg-black overflow-hidden">
        <Image
          src={Images?.contactBg} // 🔥 optimized version
          alt="contact background"
          fill
          quality={65}
          className="object-cover z-0"
          sizes="(max-width: 1024px) 100vw, 50vw"
          priority
          fetchPriority="high"
        />

        <div className="absolute inset-0 z-[1] bg-gradient-to-r from-[#000c24] via-[#02142e] to-[#0a1f4a]" />

        <div className="relative z-10 w-full h-full">
          <div className="w-full h-full min-h-[min(650px,100vh)] flex flex-col pb-[40px]">
            <Navigation />
            <div className="max-w-[2500px] m-auto w-[calc(100%_-_10%)]">
              <h1 className="self-stretch pt-[75px] text-left max-w-[18ch] justify-start text-white text-[length:var(--service-banner-title)] font-normal clash-font capitalize leading-[100%] whitespace-nowrap">
                Contact Us
                <div className="relative inline-block align-top ml-4">
                  <button
                    onClick={() => setCalendarOpen(!calendarOpen)}
                    aria-label={
                      calendarOpen ? "Close calendar" : "Open calendar"
                    }
                    className={`inline-flex cursor-pointer transition-transform duration-300 hover:scale-110 ${!calendarOpen ? "animate-jump" : ""}`}
                  >
                    {calendarOpen ? (
                      <div className="bg-white/10 p-2 rounded-full border border-white/20">
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="text-white w-[0.4em] h-[0.4em]"
                        >
                          <line x1="18" y1="6" x2="6" y2="18"></line>
                          <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                      </div>
                    ) : (
                      <Image
                        src={Images?.contactUs.calendarchat}
                        alt="Calendar"
                        width={60}
                        height={60}
                        className="w-[0.6em] h-[0.6em] object-contain"
                        sizes="(max-width: 1024px) 100vw, 50vw"
                        priority
                        fetchPriority="high"
                      />
                    )}
                  </button>

                  {!calendarOpen && (
                    <div className="absolute left-[120%] top-1/2 -translate-y-1/2 w-[200px] md:w-[280px] h-auto hidden md:block pointer-events-none z-20">
                      <Image
                        src={Images?.calendartext}
                        alt="Get a free consultation now"
                        width={220}
                        height={100}
                        className="object-contain -rotate-1"
                        sizes="(max-width: 1024px) 100vw, 50vw"
                        priority
                        fetchPriority="high"
                      />
                    </div>
                  )}
                </div>
              </h1>

              <p className="justify-start text-white/50 text-[length:var(--banner-para)] max-w-[90%] lg:max-w-[65ch] text-left font-normal leading-[150%] tracking-tight py-4">
                Talk to us for project development, building business partnerships, staffing requirements and anything technology.
              </p>

              <div className="flex flex-wrap gap-[30px] pt-[100px]">
                {ratingsData?.map((item) => (
                  <div key={item.id} className="flex gap-[10px] items-center">
                    <Image
                      src={item.img}
                      alt={item.alt}
                      width={30}
                      height={30}
                      className="w-[30px] h-auto"
                      sizes="(max-width: 1024px) 100vw, 50vw"
                      priority
                      fetchPriority="high"
                    />

                    <p className="w-[10ch] text-[#fff] text-sm">{item.title}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <DeferredVideo
          src={Videos.contact}
          className="h-full w-full mix-blend-lighten right-10 absolute object-cover translate-x-[37%] -translate-y-2/4 top-[calc(50%+40px)] [mask-image:radial-gradient(ellipse_at_center,white_60%,transparent_100%)] z-[2]"
          preload="none"
          ariaLabel="contact background video"
        />
      </section>

      <ContactSection />
      <Locations />

      {calendarOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm pointer-events-auto">
          <div
            className="absolute inset-0"
            onClick={() => setCalendarOpen(false)}
          />
          <div className="relative bg-white w-full max-w-[1000px] h-[85vh] rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300">
            <button
              onClick={() => setCalendarOpen(false)}
              aria-label="Close modal"
              className="absolute top-4 right-4 z-[101] p-2 bg-gray-100 hover:bg-gray-200 rounded-full text-black transition-colors"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-[#00040b] w-[1em] h-[1em]"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>

            <iframe
              src="https://calendly.com/connect-konstantinfo/60min?embed_domain=konstantinfo.com" // ← change localhost → your real domain in production (e.g. konstantinfo.com)
              width="100%"
              height="100%"
              frameBorder="0"
              allow="payment"
              scrolling="yes"
              title="Schedule time with us"
            ></iframe>
          </div>
        </div>
      )}
    </>
  );
}
